import type { ComponentProps } from 'react';

import { nationalNumberBr } from '@/shared/format/phone';

import { cn } from './cn';
import { fieldControlClass } from './input';
import { maskModel, maskSpec, type MaskKind } from './internal/mask-models';
import { useMaskedInput } from './internal/use-masked-input';

export type { MaskKind };

export type MaskedFieldProps = Omit<ComponentProps<'input'>, 'value' | 'defaultValue' | 'onChange' | 'type'> & {
  readonly kind: MaskKind;
  /** Valor cru: CPF 11 dígitos, CNPJ 14 caracteres maiúsculos, CEP 8 dígitos, telefone DDD + número. */
  readonly value: string | null | undefined;
  /** Recebe o cru a cada tecla, parcial inclusive (`'529982'`); a validação é do schema. */
  readonly onChange?: (raw: string) => void;
};

/**
 * CPF, CNPJ alfanumérico (`AA.AAA.AAA/AAAA-DD`), CEP ou telefone: máscara enquanto se digita,
 * valor cru no `onChange` (contrato de formatos). Cursor e Backspace nunca brigam com separador.
 */
export function MaskedField({ kind, value, onChange, onBlur, className, placeholder, ...props }: MaskedFieldProps) {
  const spec = maskSpec(kind);
  const model = maskModel(kind);
  const bindings = useMaskedInput<string>({
    model,
    value: value ?? '',
    onChange,
    onBlur,
    toValue: (significant) => significant,
    fromValue: (raw) => model.significant(kind === 'phone' ? (nationalNumberBr(raw) ?? raw) : raw),
    sameValue: (left, right) => left === right,
    onPasteText: spec.readPaste,
  });
  return (
    <input
      type="text"
      inputMode={spec.inputMode}
      autoComplete="off"
      autoCapitalize={kind === 'cnpj' ? 'characters' : 'off'}
      autoCorrect="off"
      spellCheck={false}
      placeholder={placeholder ?? spec.placeholder}
      data-slot="masked-field"
      data-mask={kind}
      className={cn(fieldControlClass, 'font-mono tabular-nums', className)}
      {...props}
      {...bindings}
    />
  );
}
